<script setup>
import { Head } from '@inertiajs/vue3'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import { months } from '@/data/content.js'

defineOptions({ layout: PublicLayout })

const seasons = [
  { key: 'peak',  name: 'June – October', label: 'Peak / long dry', body: 'The classic safari window. Sparse vegetation and animals drawn to water make for the best game viewing, and the Great Migration reaches the Mara (roughly July–October). Busiest and priciest — book ahead.' },
  { key: 'dry',   name: 'December – February', label: 'Short dry', body: 'Hot, clear and excellent for wildlife, with calving season (Jan–Feb) bringing predators close. Great for the coast too. A quieter alternative to the mid-year peak.' },
  { key: 'green', name: 'March – May', label: 'Green / long rains', body: 'The low season — lush, dramatic skies, newborn animals and far fewer people. Rain usually comes in short bursts, not all day. The best value, if you don’t mind some mud.' },
  { key: 'short', name: 'November', label: 'Short rains', body: 'Brief afternoon showers green the plains again. Shoulder-season prices, good birding, and the coast stays warm.' },
]
</script>

<template>
  <Head>
    <title>When to go · Kenya2Go</title>
    <meta name="description" content="Kenya month by month — the Great Migration, dry and green seasons, and the best time for your trip.">
  </Head>

  <div class="kp">
    <header class="kp-hero">
      <div class="kp-hero__inner">
        <p class="kp-eyebrow">When to go</p>
        <h1 class="kp-hero__title">There’s no wrong season</h1>
        <p class="kp-hero__lede">Kenya is a year-round destination — it’s about matching the month to what you want to see. Here’s the shape of the year.</p>
      </div>
    </header>

    <section class="kp-section">
      <ul class="cal">
        <li v-for="mo in months" :key="mo.m" class="cal__mo" :class="`cal__mo--${mo.s}`">
          <span>{{ mo.m }}</span>
        </li>
      </ul>
      <ul class="cal-key">
        <li><span class="dot dot--peak"></span> Peak · the Migration</li>
        <li><span class="dot dot--dry"></span> Hot &amp; dry</li>
        <li><span class="dot dot--green"></span> Green season</li>
        <li><span class="dot dot--short"></span> Short rains</li>
      </ul>

      <div class="sgrid">
        <article v-for="s in seasons" :key="s.key" class="scard">
          <span class="scard__chip" :class="`scard__chip--${s.key}`">{{ s.label }}</span>
          <h2 class="scard__name">{{ s.name }}</h2>
          <p class="scard__body">{{ s.body }}</p>
        </article>
      </div>

      <div class="sfoot">
        <p>Not sure when to come? Tell us what you want to see and we’ll suggest the month.</p>
        <a href="/contact" class="kp-btn kp-btn--solid">Ask us</a>
      </div>
    </section>
  </div>
</template>

<style scoped>
.cal { list-style: none; display: grid; grid-template-columns: repeat(12, 1fr); gap: 0.5rem; padding: 0; margin: 0 0 1.4rem; }
.cal__mo { aspect-ratio: 1 / 1.5; border-radius: var(--radius-md); display: flex; align-items: flex-end; justify-content: center; padding-bottom: 0.5rem; font-size: 0.82rem; font-weight: 600; color: oklch(0.25 0.04 50); }
.cal__mo--peak  { background: oklch(0.66 0.15 45); color: oklch(0.99 0.02 85); }
.cal__mo--dry   { background: oklch(0.84 0.13 78); }
.cal__mo--green { background: oklch(0.74 0.11 150); }
.cal__mo--short { background: oklch(0.80 0.07 200); }
.cal-key { list-style: none; display: flex; flex-wrap: wrap; gap: 1.2rem; padding: 0; margin: 0 0 3rem; font-size: 0.85rem; color: var(--inkSoft); }
.cal-key li { display: inline-flex; align-items: center; gap: 0.5rem; }
.dot { width: 0.7rem; height: 0.7rem; border-radius: 999px; }
.dot--peak { background: oklch(0.66 0.15 45); }
.dot--dry { background: oklch(0.84 0.13 78); }
.dot--green { background: oklch(0.74 0.11 150); }
.dot--short { background: oklch(0.80 0.07 200); }

.sgrid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.4rem; }
.scard { padding: 1.6rem 1.7rem; border-radius: var(--radius-lg); background: var(--surface); border: 1px solid var(--line); }
.scard__chip { display: inline-block; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; padding: 0.25rem 0.6rem; border-radius: 999px; margin-bottom: 0.8rem; color: oklch(0.25 0.04 50); }
.scard__chip--peak { background: oklch(0.66 0.15 45); color: oklch(0.99 0.02 85); }
.scard__chip--dry { background: oklch(0.84 0.13 78); }
.scard__chip--green { background: oklch(0.74 0.11 150); }
.scard__chip--short { background: oklch(0.80 0.07 200); }
.scard__name { font-family: var(--display); font-weight: 500; font-size: 1.4rem; margin: 0 0 0.6rem; color: var(--ink); }
.scard__body { font-size: 0.96rem; line-height: 1.65; color: var(--inkSoft); margin: 0; }

.sfoot { margin-top: 2.5rem; padding-top: 2rem; border-top: 1px solid var(--line); text-align: center; }
.sfoot p { color: var(--inkSoft); margin: 0 0 1rem; }

@media (max-width: 640px) { .cal { grid-template-columns: repeat(6, 1fr); } .sgrid { grid-template-columns: 1fr; } }
</style>
